




















































































import './KisweInput.scss'
import { generateRandomString } from '@/modules/common/utils'
import { computed, defineComponent, ref, toRefs } from '@vue/composition-api'
import useFlexbox from '../../compositions/flexbox'
import { KisweButton, KisweButtonColorPicker } from '../KisweButton'
import KisweIcon from '../KisweIcon'
import ValidationErrors from '../ValidationErrors'
import { Validation } from '@vuelidate/core'
import { clamp } from 'lodash'
import { useKisweInput, useSizes, useThemes } from '../../compositions'
import { KisweInputType } from '../../types'
import KisweCopyButton from '../KisweCopyButton'

const durationToString = (value: string|number): string => {
  let localValue = !!value ? Number(value) : 0
  if (Number.isNaN(localValue)) throw new Error('Number is required as input for a duration')
  if (localValue < 0) localValue = 0
  const seconds = Math.floor(localValue % 60).toString().padStart(2, '0')
  const minutes = Math.floor((localValue / 60) % 60).toString().padStart(2, '0')
  const hours = Math.floor(localValue / 3600)
  let hms = `${minutes}:${seconds}`
  if (hours > 0) hms = `${hours}:${hms}`
  return hms
}

// https://stackoverflow.com/questions/45283030/html5-input-type-time-without-am-pm-and-with-min-max
// Matches '1', '01:2', '1:2:03', '123:45:67' as 'hh:mm:ss'
const hmsRegex = /^(?:(?:(\d+):)?(\d?\d):)?(\d?\d)$/
const stringToDuration = (value: string): number|null => {
  const hmsMatch = value.match(hmsRegex)
  if (hmsMatch === null) return null
  const hours = Number.parseInt(hmsMatch[1] ?? 0)
  const minutes = Number.parseInt(hmsMatch[2] ?? 0)
  const seconds = Number.parseInt(hmsMatch[3] ?? 0)
  return (hours * 3600) + (minutes * 60) + seconds
}

/* eslint-disable max-lines */
export default defineComponent({
  name: 'KisweInput',
  components: {
    KisweButton,
    KisweButtonColorPicker,
    KisweCopyButton,
    KisweIcon,
    ValidationErrors
  },
  props: {
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    label: {
      type: String as () => string|null,
      required: false,
      default: null
    },
    type: {
      type: String as () => KisweInputType,
      required: false,
      default: KisweInputType.Text,
      validator: (value: KisweInputType) => Object.values(KisweInputType).includes(value)
    },
    value: {
      type: [ String, Number ],
      required: false,
      default: ''
    },
    placeholder: {
      type: String as () => string|null,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    min: {
      type: Number,
      required: false,
      default: Number.MIN_SAFE_INTEGER
    },
    max: {
      type: Number,
      required: false,
      default: Number.MAX_SAFE_INTEGER
    },
    step: {
      type: Number,
      required: false,
      default: 1
    },
    validator: {
      type: Object as () => Validation,
      required: false
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false
    },
    showNumberButtons: {
      type: Boolean,
      required: false,
      default: true
    },
    showCopyToClipboard: {
      type: Boolean,
      required: false,
      default: false
    },
    hasLabelEffect: {
      type: Boolean,
      required: false,
      default: false
    },
    ...useFlexbox().props,
    ...useThemes().props,
    ...useSizes().props
  },
  // eslint-disable-next-line max-lines-per-function
  setup (properties) {
    const id = `kiswe-input--${ generateRandomString(12, false) }`
    const props = toRefs(properties)
    const currentInputType = ref(props.type.value)
    const nativeInputType = computed(() => {
      const natives = [KisweInputType.Text, KisweInputType.Email, KisweInputType.Password, KisweInputType.Number]
      if (natives.includes(currentInputType.value)) return currentInputType.value
      else return KisweInputType.Text
    })
    const nativeInputName = computed(() => {
      if (props.label.value !== null) return props.label.value.replaceAll(' ', '-').toLowerCase()
      return id
    })
    const classList = computed(() => {
      const classList = [`theme-${ props.theme.value }`, `size-${ props.size.value }`]
      if (props.hasLabelEffect.value) classList.push('label-effect')
      return classList.map((c) => `kiswe-input--${ c }`)
    })
    const nativePlaceholder = computed(() => {
      if (props.placeholder.value !== null) return props.placeholder.value
      if (currentInputType.value === KisweInputType.Duration) return 'mm:ss'
      else return props.label.value ?? ''
    })
    const nativePattern = computed(() => {
      switch (currentInputType.value) {
        // case KisweInputType.Email: return '' // TODO: add
        // case KisweInputType.Color: return '' // TODO: add
        case KisweInputType.Duration: return '(\\d+:)?\\d?\\d:\\d?\\d'
        default: return '(.*?)'
      }
    })
    const togglePasswordType = () => {
      if (props.type.value !== KisweInputType.Password) return
      if (currentInputType.value === KisweInputType.Password) currentInputType.value = KisweInputType.Text
      else currentInputType.value = KisweInputType.Password
    }
    return {
      classList,
      currentInputType,
      id,
      nativeInputName,
      nativeInputType,
      nativePattern,
      nativePlaceholder,
      togglePasswordType,
      ...useKisweInput(props.type)
    }
  },
  computed: {
    val: {
      get (): string {
        if (this.isInputTypeColor && this.value === '') return '#'
        if (this.isInputTypeDuration) return durationToString(this.value)
        if (this.value === null) return ''
        if (this.isInputTypeNumber && Number.isNaN(this.value)) return '0'
        return `${this.value}`
      },
      // eslint-disable-next-line complexity
      set (newVal: string) {
        if (this.isInputTypeColor && this.value === '') {
          this.$emit('input', '#')
        } else if (this.isInputTypeDuration) {
          const duration = stringToDuration(newVal)
          if (duration !== null) this.$emit('input', duration)
        } else if (this.isInputTypeAnyString) {
          this.$emit('input', newVal)
        } else if (this.isInputTypeNumber) {
          const val = (newVal === '') ? 0 : Number.parseFloat(newVal)
          if (!Number.isNaN(val) && (val < this.min || val > this.max)) return
          if (Number.isNaN(val)) {
            this.$emit('input', '')
          } else {
            this.$emit('input', val)
          }
        }
      }
    }
  },
  mounted () {
    if (this.validator) {
      console.warn('You are using the old "validator" prop. Please set the ValidationErrors component in ' +
        'the "errors" slot on KisweFormField instead.')
    }
    if (!this.isInputTypeNumber) return
    const numberValue = Number.parseFloat(`${this.value}`)
    if (Number.isNaN(numberValue)) return
    const clampedValue = clamp(numberValue, this.min, this.max).toString()
    if (clampedValue !== this.val) this.val = clampedValue
  },
  methods: {
    onBlur (event: Event) {
      const element = event.target as HTMLInputElement|undefined
      let value = element?.value ?? ''
      if (this.isInputTypeDuration) {
        value = `${ stringToDuration(value) ?? 0 }`
      } else if (this.isInputTypeNumber) {
        const clampedValue = clamp(Number.parseFloat(value), this.min, this.max)
        value = Number.isNaN(clampedValue) ? '' : clampedValue.toString()
        if (this.val !== value) this.val = value
      }
      this.$emit('change', value)
    },
    increaseValue () {
      if (!this.isInputTypeNumber) return
      const newVal = Number.parseFloat(this.val) + this.step
      this.val = `${ newVal }`
    },
    decreaseValue () {
      if (!this.isInputTypeNumber) return
      const newVal = Number.parseFloat(this.val) - this.step
      this.val = `${ newVal }`
    }
  }
})
